import React, {
  FC,
  MutableRefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  DrugItem,
  Filter,
  FirstCategoryLevel,
  SecondCategoryLevel,
  SelectedFilter
} from 'src/api/types';
import cn from 'classnames';
import NextImage from 'src/shared-components/NextImage/NextImage';
import CloseButton from 'src/shared-components/Modal/CloseButton';
import Pagination from 'src/shared-components/Pagination/Pagination';
import { useRouter } from 'next/router';
import { defaultPageSize } from 'src/api/requests/postDrugSearch';
import {
  ModalPosition,
  ModalRounding
} from 'src/shared-components/Modal/Modal';
import { GeneratedLinkUrlObject, SearchPageQueryType } from 'src/utils/links';
import { FormattedMessage } from 'react-intl';

import dynamic from 'next/dynamic';
import useModalStore from 'src/hooks/useModalStore';
import { GTAGPrefix } from 'src/hooks/useGTAG';
import isEmpty from 'lodash/isEmpty';
import SecondLevelCategoryList from 'src/page-components/category/components/SecondLevelCategoryList';
import ThirdLevelCategoryList from 'src/page-components/category/components/ThirdLevelCategoryList';
import SearchResultsContainer from './SearchResultsContainer';
import { parseQuerifiedFilters } from '../utils';
import SortingSelect from './SortingSelect';
import searchTrns from '../trns';

const filterButton = 'search/filter-button.svg';

const Modal = dynamic(() => import('src/shared-components/Modal/Modal'), {
  ssr: false
});

const FiltersContainer = dynamic(() => import('./FiltersContainer'));

interface Props {
  filters: Filter[];
  drugList: DrugItem[];
  selectedFirstLevelCategory?: FirstCategoryLevel;
  selectedSecondLevelCategory?: SecondCategoryLevel;
  gtagPrefix?: GTAGPrefix;
  totalDrugsCount: number;
  selectedPages: number[];
  isLoading: boolean;
  handleLoadMoreSubmit: (page: number) => void;
  getPaginationLink: (page: number) => GeneratedLinkUrlObject;
  pageLinkGenerationMethod: (
    query: SearchPageQueryType
  ) => GeneratedLinkUrlObject;
  textSearch?: string;
  isFiltersLoading: boolean;
  isAnalogBannerVisible?: boolean;
  isTradenameBannerVisible?: boolean;
  analogsRef?: MutableRefObject<HTMLDivElement>;
  isHidePaginationAllow?: boolean;
}

const SearchResultsSection: FC<Props> = ({
  filters,
  drugList,
  selectedFirstLevelCategory,
  selectedSecondLevelCategory,
  gtagPrefix,
  totalDrugsCount,
  selectedPages,
  isLoading,
  handleLoadMoreSubmit,
  getPaginationLink,
  pageLinkGenerationMethod,
  textSearch,
  isFiltersLoading,
  isAnalogBannerVisible,
  analogsRef,
  isHidePaginationAllow = false,
  isTradenameBannerVisible = false
}) => {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);
  const { updateModalsConfig } = useModalStore();
  const router = useRouter();
  const lastPageIndex = Math.ceil(totalDrugsCount / defaultPageSize);

  const refCurrent = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const newFilters =
      parseQuerifiedFilters(router?.query?.filters as string) || [];
    setSelectedFilters(newFilters);
  }, [router?.query?.filters]);

  const onSubmitFilters = () => {
    updateModalsConfig('search-result-selection-modal', false);
  };

  return (
    <>
      <div className="flex w-full relative">
        <button
          className={cn('fixed top-0 left-0', 'lm:hidden')}
          ref={refCurrent}
        />
        <Modal
          id="search-result-selection-modal"
          withCloseIcon={true}
          position={ModalPosition.RelativeBottom}
          roundingMode={ModalRounding.Bottom}
          modalContainerId="modal-root"
          onClose={() =>
            updateModalsConfig('search-result-selection-modal', false)
          }
          triggerElementRef={refCurrent}
          globalContainerClassName="lm:hidden"
        >
          <FiltersContainer
            isLoading={isFiltersLoading}
            drugList={drugList}
            filters={filters}
            selectedFilters={selectedFilters}
            pageLinkGenerationMethod={pageLinkGenerationMethod}
            onSubmitFilters={onSubmitFilters}
            textSearch={textSearch}
          />
        </Modal>
        <div
          className={cn(
            'absolute left-0 z-20 w-72 flex flex-col pr-7.5 bg-white shadow p-5 rounded-3xl',
            'xl:w-1/4 lm:block lm:relative lm:bg-transparent lm:shadow-none lm:p-0 lm:pr-5',
            'hidden'
          )}
        >
          <p className="fs-caption-reg text-shuttleGray ml-4 pb-2 border-b border-gallery">
            <FormattedMessage
              {...searchTrns.productsFound}
              values={{ count: totalDrugsCount }}
            />
          </p>

          <div className="absolute top-4 right-4 w-4 h-4 rounded-full shadow lm:hidden">
            <CloseButton
              onClose={() =>
                updateModalsConfig('search-result-selection-modal', false)
              }
            />
          </div>

          {selectedFirstLevelCategory?.subCategories &&
            !selectedSecondLevelCategory && (
              <div className="hidden lm:block">
                <SecondLevelCategoryList
                  selectedFirstLevelCategory={selectedFirstLevelCategory}
                  selectedSecondLevelCategory={
                    selectedFirstLevelCategory?.subCategories
                  }
                />
              </div>
            )}

          {selectedSecondLevelCategory?.subCategories && (
            <div className="hidden lm:block">
              <ThirdLevelCategoryList
                selectedFirstLevelCategory={selectedFirstLevelCategory}
                selectedSecondLevelCategory={selectedSecondLevelCategory}
                selectedThirdLevelCategory={
                  selectedSecondLevelCategory?.subCategories
                }
              />
            </div>
          )}

          <FiltersContainer
            isLoading={isFiltersLoading}
            drugList={drugList}
            filters={filters}
            selectedFilters={selectedFilters}
            pageLinkGenerationMethod={pageLinkGenerationMethod}
            textSearch={textSearch}
            containerClassName="hidden lm:flex"
          />
        </div>

        <div className="w-full lm:w-3/4">
          <p className="lm:hidden fs-caption-reg text-shuttleGray border-b border-secondary pb-3 mx-4 mb-3.5">
            <FormattedMessage
              {...searchTrns.productsFound}
              values={{ count: totalDrugsCount }}
            />
          </p>

          {selectedFirstLevelCategory?.subCategories &&
            !selectedSecondLevelCategory && (
              <div className="lm:hidden">
                <SecondLevelCategoryList
                  selectedFirstLevelCategory={selectedFirstLevelCategory}
                  selectedSecondLevelCategory={
                    selectedFirstLevelCategory?.subCategories
                  }
                  defaultItemsDisplayed={5}
                  openModal={() =>
                    updateModalsConfig('second-level-category-list', true)
                  }
                />
              </div>
            )}

          <Modal
            withCloseIcon={true}
            position={ModalPosition.RelativeBottom}
            roundingMode={ModalRounding.Bottom}
            modalContainerId="modal-root"
            onClose={() =>
              updateModalsConfig('second-level-category-list', false)
            }
            triggerElementRef={refCurrent}
            title={
              <div className="flex w-full items-center fs-header6 uppercase">
                {selectedFirstLevelCategory?.name || ''}
              </div>
            }
            childrenContainerClass="px-0"
            id="second-level-category-list"
          >
            <SecondLevelCategoryList
              selectedFirstLevelCategory={selectedFirstLevelCategory}
              selectedSecondLevelCategory={
                selectedFirstLevelCategory?.subCategories
              }
              defaultItemsDisplayed={
                selectedFirstLevelCategory?.subCategories?.length
              }
              closeModal={() =>
                updateModalsConfig('second-level-category-list', false)
              }
            />
          </Modal>

          {selectedSecondLevelCategory?.subCategories && (
            <div className="lm:hidden">
              <ThirdLevelCategoryList
                selectedFirstLevelCategory={selectedFirstLevelCategory}
                selectedSecondLevelCategory={selectedSecondLevelCategory}
                selectedThirdLevelCategory={
                  selectedSecondLevelCategory?.subCategories
                }
                defaultItemsDisplayed={5}
                openModal={() =>
                  updateModalsConfig('third-level-category-list', true)
                }
              />
            </div>
          )}
          <Modal
            withCloseIcon={true}
            position={ModalPosition.RelativeBottom}
            roundingMode={ModalRounding.Bottom}
            modalContainerId="modal-root"
            onClose={() =>
              updateModalsConfig('third-level-category-list', false)
            }
            triggerElementRef={refCurrent}
            title={
              <div className="flex w-full items-center fs-header6 uppercase">
                {selectedSecondLevelCategory?.name || ''}
              </div>
            }
            childrenContainerClass="px-0"
            id="third-level-category-list"
          >
            <ThirdLevelCategoryList
              selectedFirstLevelCategory={selectedFirstLevelCategory}
              selectedSecondLevelCategory={selectedSecondLevelCategory}
              selectedThirdLevelCategory={
                selectedSecondLevelCategory?.subCategories
              }
              defaultItemsDisplayed={
                selectedSecondLevelCategory?.subCategories?.length
              }
              closeModal={() =>
                updateModalsConfig('third-level-category-list', false)
              }
            />
          </Modal>

          <div className="flex relative lm:border-b lm:border-t-0 border-gallery justify-between lm:justify-end px-4 pt-3 lm:px-0 lm:pt-0">
            <div className="flex lm:hidden items-center">
              <button
                className="pr-1.5"
                disabled={
                  isLoading ||
                  isFiltersLoading ||
                  isEmpty(drugList) ||
                  isEmpty(filters)
                }
                onClick={() =>
                  updateModalsConfig('search-result-selection-modal', true)
                }
              >
                <NextImage
                  alt="filter"
                  src={filterButton}
                  layout="fixed"
                  height={24}
                  width={24}
                  priority
                />
              </button>
              <span className="fs-caption-reg text-shuttleGray">
                <FormattedMessage {...searchTrns.filter} />
              </span>
            </div>
            <SortingSelect
              parentElementRef={refCurrent}
              pageLinkGenerationMethod={pageLinkGenerationMethod}
              textSearch={textSearch}
              gtagPrefix={gtagPrefix}
            />
          </div>
          <SearchResultsContainer
            textSearch={textSearch}
            drugList={drugList}
            gtagPrefix={gtagPrefix}
            filters={filters}
            selectedFilters={selectedFilters}
            totalDrugsCount={totalDrugsCount}
            selectedPages={selectedPages}
            lastPageReached={
              selectedPages[selectedPages?.length - 1] === lastPageIndex
            }
            isLoading={isLoading}
            handleLoadMoreSubmit={handleLoadMoreSubmit}
            pageLinkGenerationMethod={pageLinkGenerationMethod}
            isAnalogBannerVisible={isAnalogBannerVisible}
            isTradenameBannerVisible={isTradenameBannerVisible}
            analogsRef={analogsRef}
          />
          {!(isHidePaginationAllow && lastPageIndex === 1) && (
            <div className="flex justify-center">
              <Pagination
                selectedPages={selectedPages}
                lastPageIndex={lastPageIndex}
                getPaginationLink={getPaginationLink}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchResultsSection;
